import { useCallback, useEffect, useMemo, useState } from 'react';
import TimePicker from 'react-time-picker';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import API from '../../service/api';
import { CustomSelect } from '../CustomSelect';
import { ws } from '../../service/ws';
import { MDBCheckbox, MDBInput } from 'mdb-react-ui-kit';
import { CustomModal } from '../CustomModal';
import { LoadingSpinner } from '../LoadingSpinner';
import { Alert } from '../Alert';

export interface AdvancedSettingsModalProps {
    open: boolean;
    groupId?: number;
    locationId?: number;
    machineId: number;
    onClose: () => void;
}
export const AdvancedSettingsModal = ({
    open,
    groupId,
    locationId,
    machineId,
    onClose,
}: AdvancedSettingsModalProps) => {
    const [params, setParams] = useState<any[] | null>(null);
    const [selectedParamAddr, setSelectedParamAddr] = useState<number | 'raw'>(
        -1,
    );
    const [selectedParam, setSelectedParam] = useState<any | null>(null);
    const [rawAddr, setRawAddr] = useState(0);
    const [currentValue, setCurrentValue] = useState<any | null>(null);
    const [selectedSchedule, setSelectedSchedule] = useState(-1);
    const [valuesWaiting, setValuesWaiting] = useState<{
        [addr: number | string]: boolean;
    }>({});
    const [valuesWriting, setValuesWriting] = useState<{
        [addr: number | string]: boolean;
    }>({});
    const [addrValues, setAddrValues] = useState<{
        [addr: number | string]: number;
    }>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [changed, setChanged] = useState(false);
    const [writeMessage, setWriteMessage] = useState('');
    const { t } = useTranslation();

    const uri = useMemo(
        () =>
            groupId
                ? `/api/groups/${groupId}/${locationId}/${machineId}`
                : `/api/admmachines/${machineId}`,
        [groupId, locationId, machineId],
    );

    useEffect(() => {
        // const unlisten = WS.advancedSettingsListener({
        //     groupId,
        //     locationId,
        //     machineId,
        //     onWrite: ({ addr }: any) =>
        //         setValuesWriting((prev) => ({ ...prev, [addr]: false })),
        //     onRead: ({ addr, value }: any) => {
        //         setValuesWaiting((prev) => ({ ...prev, [addr]: false }));
        //         setAddrValues((prev) => ({ ...prev, [addr]: value }));
        //     },
        // });
        // return () => unlisten();
    }, [groupId, locationId, machineId]);

    useEffect(() => {
        if (!open) return;
        setSelectedParamAddr(-1);
        setSelectedParam(null);

        setValuesWaiting({});
        setValuesWriting({});
        setAddrValues({});

        API.GET(`${uri}/advanced`)
            .then(setParams)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [open]);

    useEffect(() => {
        const addrToUpdate =
            selectedParam?.type == 'vtRowMem' ? rawAddr : selectedParamAddr;
        if (addrValues[addrToUpdate] !== undefined && currentValue == null)
            setCurrentValue(addrValues[addrToUpdate]);

        if (
            selectedParam?.type == 'vtSchedule' &&
            currentValue &&
            currentValue.length > 0 &&
            selectedSchedule < 0
        )
            setSelectedSchedule(0);
        if (
            selectedParam?.type == 'vtHolidays' &&
            currentValue &&
            currentValue.length > 0 &&
            selectedSchedule < 0
        )
            setSelectedSchedule(0);
    }, [addrValues, currentValue]);

    const read = useCallback(() => {
        setCurrentValue(null);
        setSelectedSchedule(-1);
        const addrToRead =
            selectedParam?.type == 'vtRowMem' ? rawAddr : selectedParamAddr;
        setAddrValues((prev) => {
            delete prev[addrToRead];
            return prev;
        });

        API.GET(`${uri}/fram/${addrToRead}`)
            .then(() => {
                const prevState = valuesWaiting;
                prevState[addrToRead] = true;
                setValuesWaiting({ ...prevState });
            })
            .catch((err) => setError(err.message));
    }, [selectedParam, rawAddr, uri]);

    const write = useCallback(() => {
        if (selectedParam?.type == 'vtPassword' && currentValue.length !== 4)
            return setError('Invalid password: must be 4 numeric characters.');

        setChanged(false);
        const addrToWrite =
            selectedParam?.type == 'vtRowMem' ? rawAddr : selectedParamAddr;

        API.POST(`${uri}/fram/${addrToWrite}`, { val: currentValue })
            .then(() => {
                setValuesWriting((prev) => ({ ...prev, [addrToWrite]: true }));
            })
            .catch((err) => setError(err.message))
            .finally(() => {});
    }, [selectedParam, rawAddr, currentValue, currentValue, uri]);

    useEffect(() => {
        setChanged(false);

        setError('');
        setWriteMessage('');
        if (!params) return;
        if (selectedParamAddr === 'raw') {
            const param = params.find((p) => p.type == 'vtRowMem');
            if (!param) return;
            setSelectedParam(param);
            setCurrentValue(
                addrValues[rawAddr] !== undefined ? addrValues[rawAddr] : null,
            );
        } else {
            setCurrentValue(
                addrValues[selectedParamAddr] !== undefined
                    ? addrValues[selectedParamAddr]
                    : null,
            );
            const param = params.find((p) => p.addr == selectedParamAddr);
            if (!param) return;
            setSelectedParam(param);
        }
    }, [selectedParamAddr, params]);

    const setNthBit = useCallback(
        (bit: number, val: boolean) => {
            if (val) {
                setCurrentValue(currentValue | (1 << bit));
            } else {
                setCurrentValue(currentValue & ~(1 << bit));
            }
        },
        [currentValue],
    );

    const passwordInput = useCallback((val: string) => {
        setCurrentValue(val.replace(/[^0-9]/g, '').substr(0, 4));
    }, []);

    const scheduleTitle = useCallback((idx: number, val: number) => {
        const on =
            {
                0: 'OFF',
                1: 'ON',
            }[scheduleOnOffValue(val)] || '?';

        const days = val >> 24;
        let daysStr = '';
        const daysStrDict: { [key: number]: string } = {
            0: 'Mo',
            1: 'Tu',
            2: 'We',
            3: 'Th',
            4: 'Fr',
            5: 'Sa',
            6: 'Su',
            7: 'Ho',
        };
        for (let i = 0; i < 8; i++)
            daysStr += ((days >> i) & 1) === 1 ? daysStrDict[i] : '';
        if (daysStr == '') return 'N/A';

        return `${on} - ${scheduleTimeString(val)} ${daysStr}`;
    }, []);

    const holidayDate = useCallback((val: number) => {
        const now = new Date();
        const day = (val >> 24) & 0xff,
            month = (val >> 16) & 0xff,
            year = val & 0xffff || now.getFullYear();
        if (day == 0 || month == 0) return null;
        else return new Date(year, month - 1, day);
    }, []);

    const holidaySetDate = useCallback((idx: number, date: Date) => {
        if (!date) return;
        const day = date.getDate(),
            month = date.getMonth() + 1,
            year = date.getFullYear();

        setCurrentValue((prev: number[]) =>
            prev.map((el, i) =>
                i !== idx ? el : (day << 24) + (month << 16) + year,
            ),
        );
        setChanged(true);
    }, []);

    const holidaySetRecurrent = useCallback((idx: number) => {
        setCurrentValue((prev: number[]) =>
            prev.map((el, i) => (i !== idx ? el : el & 0xffff0000)),
        );
        setChanged(true);
    }, []);

    const holidayTitle = useCallback((val: number) => {
        const day = (val >> 24) & 0xff,
            month = (val >> 16) & 0xff,
            year = val & 0xffff;
        if (day == 0 || month == 0) return 'N/A';
        return `${day < 10 ? '0' : ''}${day}.${month < 10 ? '0' : ''}${month}${year > 0 ? `.${year}` : ''}`;
    }, []);

    const scheduleTimeString = useCallback((val: number) => {
        let clock = val & 0xffff;
        if (clock >= 1440) {
            console.error(`Invalid clock value: ` + clock);
            clock = 0;
        }
        const hours = Math.floor(clock / 60),
            mins = clock - hours * 60;
        const hoursString = hours < 10 ? `0${hours}` : hours,
            minsString = mins < 10 ? `0${mins}` : mins;
        return `${hoursString}:${minsString}`;
    }, []);

    const scheduleOnOffValue = useCallback((val: number) => {
        return ((val >> 16) & 0x01) == 1 ? 1 : 0;
    }, []);

    const setScheduleOnOffValue = useCallback((idx: number, on: boolean) => {
        if (on) {
            setCurrentValue((prev: number[]) =>
                prev.map((el, i) =>
                    i !== idx ? el : (el & 0xff00ffff) | 0x00010000,
                ),
            );
        } else {
            setCurrentValue((prev: number[]) =>
                prev.map((el, i) => (i !== idx ? el : el & 0xff00ffff)),
            );
        }
        setChanged(true);
    }, []);

    const timePickerHandler = useCallback((idx: number, str: string) => {
        if (!str) return;
        const time = Number(str.split(':')[0]) * 60 + Number(str.split(':')[1]);
        const clock = currentValue[idx];
        const newClock = (clock & 0xffff0000) | time;

        setCurrentValue((prev: number[]) =>
            prev.map((el, i) => (i == idx ? newClock : el)),
        );
        setChanged(true);
    }, []);

    const setNthBitOfSchedule = useCallback(
        (idx: number, bit: number, val: boolean) => {
            if (val) {
                setCurrentValue((prev: number[]) =>
                    prev.map((el, i) => (i !== idx ? el : el | (1 << bit))),
                );
            } else {
                setCurrentValue((prev: number[]) =>
                    prev.map((el, i) => (i !== idx ? el : el & ~(1 << bit))),
                );
            }
        },
        [],
    );

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('advanced_settings')}
        >
            {(!params || loading) && <LoadingSpinner />}

            {params && !loading && (
                <>
                    <div className="d-flex flex-column">
                        <CustomSelect
                            title={t('parameter')}
                            items={params.map((p) => ({
                                val: p.addr ? p.addr : 'raw',
                                title: p.name,
                            }))}
                            selected={
                                ['number', 'string'].includes(
                                    typeof selectedParamAddr,
                                )
                                    ? selectedParamAddr
                                    : null
                            }
                            onSelect={(addr) =>
                                setSelectedParamAddr(Number(addr))
                            }
                            search={true}
                        />
                        {selectedParamAddr && (
                            <>
                                {[
                                    'vtByte',
                                    'vtWORD',
                                    'vtDWORD',
                                    'vtString',
                                    'vtPassword',
                                ].includes(selectedParam?.type) && (
                                    <>
                                        <div className="d-flex flex-row align-items-center mt-2">
                                            <MDBInput
                                                type={
                                                    currentValue == null
                                                        ? 'text'
                                                        : [
                                                                'vtString',
                                                                'vtPassword',
                                                            ].includes(
                                                                selectedParam?.type,
                                                            )
                                                          ? 'text'
                                                          : 'number'
                                                }
                                                name="val"
                                                id="val"
                                                value={
                                                    currentValue !== null
                                                        ? currentValue
                                                        : t('no_data')
                                                }
                                                disabled={
                                                    currentValue == null ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]
                                                }
                                                onInput={(ev) => {
                                                    if (
                                                        selectedParam?.type ===
                                                        'vtPassword'
                                                    )
                                                        passwordInput(
                                                            ev.currentTarget
                                                                .value,
                                                        );
                                                    else
                                                        setCurrentValue(
                                                            ev.currentTarget
                                                                .value,
                                                        );
                                                    setChanged(true);
                                                    setWriteMessage('');
                                                    setError('');
                                                }}
                                                label={t('value')}
                                            />
                                            {!valuesWaiting[
                                                selectedParamAddr
                                            ] &&
                                                !valuesWriting[
                                                    selectedParamAddr
                                                ] && (
                                                    <span
                                                        role="button"
                                                        onClick={() => read()}
                                                        className="text-primary ms-1"
                                                    >
                                                        <i className="fas fa-upload fa-lg" />
                                                    </span>
                                                )}
                                            {(valuesWaiting[
                                                selectedParamAddr
                                            ] ||
                                                valuesWriting[
                                                    selectedParamAddr
                                                ]) && (
                                                <div
                                                    className="spinner-border spinner-border-sm me-1 ms-1"
                                                    role="status"
                                                >
                                                    <span className="visually-hidden">
                                                        {t('loading...')}
                                                    </span>
                                                </div>
                                            )}
                                            {currentValue !== null &&
                                                changed &&
                                                !valuesWriting[
                                                    selectedParamAddr
                                                ] &&
                                                !selectedParam?.readOnly && (
                                                    <span
                                                        role="button"
                                                        onClick={() => write()}
                                                        className="text-success ms-1"
                                                    >
                                                        <i className="fas fa-download fa-lg" />
                                                    </span>
                                                )}
                                        </div>
                                    </>
                                )}
                                {['vt8YN'].includes(selectedParam?.type) && (
                                    <>
                                        <div className="d-flex flex-column">
                                            {selectedParam?.items?.map(
                                                (it: string, idx: number) => (
                                                    <MDBCheckbox
                                                        key={
                                                            selectedParamAddr +
                                                            String(idx)
                                                        }
                                                        className="ms-1"
                                                        type="checkbox"
                                                        name={
                                                            'checkname.' +
                                                            selectedParamAddr +
                                                            idx
                                                        }
                                                        checked={
                                                            currentValue !==
                                                                null &&
                                                            Boolean(
                                                                currentValue &
                                                                (1 << idx),
                                                            )
                                                        }
                                                        id={
                                                            'check.' +
                                                            selectedParamAddr +
                                                            idx
                                                        }
                                                        disabled={
                                                            currentValue ==
                                                                null ||
                                                            valuesWriting[
                                                                selectedParamAddr
                                                            ]
                                                        }
                                                        onChange={(ev) => {
                                                            setNthBit(
                                                                idx,
                                                                ev.currentTarget
                                                                    .checked,
                                                            );
                                                            setChanged(true);
                                                        }}
                                                        label={it}
                                                    />
                                                ),
                                            )}
                                            <div className="d-flex flex-row justify-content-end">
                                                {!valuesWaiting[
                                                    selectedParamAddr
                                                ] &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                read()
                                                            }
                                                            className="text-primary ms-1"
                                                        >
                                                            <i className="fas fa-upload fa-lg" />
                                                        </span>
                                                    )}
                                                {(valuesWaiting[
                                                    selectedParamAddr
                                                ] ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]) && (
                                                    <div
                                                        className="spinner-border spinner-border-sm me-1 ms-1"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            {t('loading...')}
                                                        </span>
                                                    </div>
                                                )}
                                                {currentValue !== null &&
                                                    changed &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] &&
                                                    !selectedParam?.readOnly && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                write()
                                                            }
                                                            className="text-success ms-1"
                                                        >
                                                            <i className="fas fa-download fa-lg" />
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {['vtColors'].includes(selectedParam?.type) && (
                                    <>
                                        <div className="d-flex flex-row align-items-center">
                                            <CustomSelect
                                                items={Object.keys(
                                                    selectedParam.colors,
                                                ).map((c) => ({
                                                    val: selectedParam.colors[
                                                        c
                                                    ],
                                                    title: c,
                                                }))}
                                                defaultTitle={t(
                                                    'upload_from_machine',
                                                )}
                                                selected={currentValue}
                                                disabled={
                                                    currentValue == null ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]
                                                }
                                                onSelect={(c) => {
                                                    setCurrentValue(c);
                                                    setChanged(true);
                                                }}
                                            />
                                            {!valuesWaiting[
                                                selectedParamAddr
                                            ] &&
                                                !valuesWriting[
                                                    selectedParamAddr
                                                ] && (
                                                    <span
                                                        role="button"
                                                        onClick={() => read()}
                                                        className="text-primary ms-1"
                                                    >
                                                        <i className="fas fa-upload fa-lg" />
                                                    </span>
                                                )}
                                            {(valuesWaiting[
                                                selectedParamAddr
                                            ] ||
                                                valuesWriting[
                                                    selectedParamAddr
                                                ]) && (
                                                <div
                                                    className="spinner-border spinner-border-sm me-1 ms-1"
                                                    role="status"
                                                >
                                                    <span className="visually-hidden">
                                                        {t('loading...')}
                                                    </span>
                                                </div>
                                            )}
                                            {currentValue !== null &&
                                                changed &&
                                                !valuesWriting[
                                                    selectedParamAddr
                                                ] &&
                                                !selectedParam?.readOnly && (
                                                    <span
                                                        role="button"
                                                        onClick={() => write()}
                                                        className="text-success ms-1"
                                                    >
                                                        <i className="fas fa-download fa-lg" />
                                                    </span>
                                                )}
                                        </div>
                                    </>
                                )}
                                {['vtRowMem'].includes(selectedParam?.type) && (
                                    <>
                                        <div className="d-flex flex-row mt-2 align-items-center">
                                            <MDBInput
                                                type="number"
                                                name="raw"
                                                id="raw"
                                                className="me-1"
                                                value={rawAddr}
                                                disabled={
                                                    valuesWriting[rawAddr]
                                                }
                                                onInput={(ev) => {
                                                    setRawAddr(
                                                        Number(
                                                            ev.currentTarget
                                                                .value,
                                                        ),
                                                    );
                                                    setChanged(true);
                                                    setWriteMessage('');
                                                    setError('');
                                                }}
                                                label={t('mem_address')}
                                            />

                                            <MDBInput
                                                type={
                                                    currentValue == null
                                                        ? 'text'
                                                        : 'number'
                                                }
                                                name="val"
                                                id="val"
                                                style={{ maxWidth: 100 }}
                                                value={
                                                    currentValue !== null
                                                        ? currentValue
                                                        : t('no_data')
                                                }
                                                disabled={
                                                    currentValue == null ||
                                                    valuesWriting[rawAddr]
                                                }
                                                onInput={(ev) => {
                                                    setCurrentValue(
                                                        ev.currentTarget.value,
                                                    );
                                                    setChanged(true);
                                                    setWriteMessage('');
                                                    setError('');
                                                }}
                                                label={t('value')}
                                            />

                                            {!valuesWaiting[rawAddr] &&
                                                !valuesWriting[rawAddr] && (
                                                    <span
                                                        role="button"
                                                        onClick={() => read()}
                                                        className="text-primary ms-1"
                                                    >
                                                        <i className="fas fa-upload fa-lg" />
                                                    </span>
                                                )}
                                            {(valuesWaiting[rawAddr] ||
                                                valuesWriting[rawAddr]) && (
                                                <div
                                                    className="spinner-border spinner-border-sm me-1 ms-1"
                                                    role="status"
                                                >
                                                    <span className="visually-hidden">
                                                        {t('loading...')}
                                                    </span>
                                                </div>
                                            )}
                                            {currentValue !== null &&
                                                changed &&
                                                !valuesWriting[rawAddr] &&
                                                !selectedParam?.readOnly && (
                                                    <span
                                                        role="button"
                                                        onClick={() => write()}
                                                        className="text-success ms-1"
                                                    >
                                                        <i className="fas fa-download fa-lg" />
                                                    </span>
                                                )}
                                        </div>
                                    </>
                                )}
                                {['vtSchedule'].includes(
                                    selectedParam?.type,
                                ) && (
                                    <>
                                        <div className="d-flex flex-column">
                                            <div className="d-flex flex-row align-items-center">
                                                <CustomSelect
                                                    items={
                                                        !Array.isArray(
                                                            currentValue,
                                                        )
                                                            ? []
                                                            : currentValue.map(
                                                                  (
                                                                      val,
                                                                      idx,
                                                                  ) => ({
                                                                      val: idx,
                                                                      title: scheduleTitle(
                                                                          idx,
                                                                          val,
                                                                      ),
                                                                  }),
                                                              )
                                                    }
                                                    w100
                                                    defaultTitle={t(
                                                        'upload_from_machine',
                                                    )}
                                                    selected={selectedSchedule}
                                                    onSelect={(s) =>
                                                        setSelectedSchedule(
                                                            Number(s),
                                                        )
                                                    }
                                                    disabled={
                                                        currentValue == null ||
                                                        valuesWriting[
                                                            selectedParamAddr
                                                        ]
                                                    }
                                                />
                                            </div>
                                            {selectedSchedule >= 0 &&
                                                currentValue !== null && (
                                                    <>
                                                        <div className="d-flex flex-row">
                                                            <div className="d-flex flex-column col-8 align-items-start">
                                                                {[
                                                                    t('monday'),
                                                                    t(
                                                                        'tuesday',
                                                                    ),
                                                                    t(
                                                                        'wednesday',
                                                                    ),
                                                                    t(
                                                                        'thursday',
                                                                    ),
                                                                    t('friday'),
                                                                    t(
                                                                        'saturday',
                                                                    ),
                                                                    t('sunday'),
                                                                    t(
                                                                        'holidays',
                                                                    ),
                                                                ].map(
                                                                    (
                                                                        day,
                                                                        idx,
                                                                    ) => (
                                                                        <MDBCheckbox
                                                                            key={
                                                                                day
                                                                            }
                                                                            type="checkbox"
                                                                            className="ms-1 ps-0"
                                                                            name={
                                                                                'checkname.' +
                                                                                selectedSchedule +
                                                                                'day' +
                                                                                idx
                                                                            }
                                                                            checked={
                                                                                currentValue !==
                                                                                    null &&
                                                                                Boolean(
                                                                                    currentValue[
                                                                                        selectedSchedule
                                                                                    ] &
                                                                                    (1 <<
                                                                                        (24 +
                                                                                            idx)),
                                                                                )
                                                                            }
                                                                            id={
                                                                                'check.' +
                                                                                selectedSchedule +
                                                                                'day' +
                                                                                idx
                                                                            }
                                                                            disabled={
                                                                                currentValue ==
                                                                                    null ||
                                                                                valuesWriting[
                                                                                    selectedParamAddr
                                                                                ]
                                                                            }
                                                                            onChange={(
                                                                                ev,
                                                                            ) => {
                                                                                setNthBitOfSchedule(
                                                                                    selectedSchedule,
                                                                                    24 +
                                                                                        idx,
                                                                                    ev
                                                                                        .currentTarget
                                                                                        .checked,
                                                                                );
                                                                                setChanged(
                                                                                    true,
                                                                                );
                                                                            }}
                                                                            label={
                                                                                day
                                                                            }
                                                                        />
                                                                    ),
                                                                )}
                                                            </div>
                                                            <div className="d-flex flex-column col-4 py-2">
                                                                <TimePicker
                                                                    value={scheduleTimeString(
                                                                        currentValue[
                                                                            selectedSchedule
                                                                        ],
                                                                    )}
                                                                    format="HH:mm"
                                                                    clearIcon={
                                                                        null
                                                                    }
                                                                    clockIcon={
                                                                        null
                                                                    }
                                                                    disableClock={
                                                                        true
                                                                    }
                                                                    onChange={(
                                                                        t,
                                                                    ) =>
                                                                        timePickerHandler(
                                                                            selectedSchedule,
                                                                            t?.toString() ||
                                                                                '',
                                                                        )
                                                                    }
                                                                />
                                                                <span>
                                                                    <CustomSelect
                                                                        items={[
                                                                            {
                                                                                val: 0,
                                                                                title: t(
                                                                                    'off',
                                                                                ),
                                                                            },
                                                                            {
                                                                                val: 1,
                                                                                title: t(
                                                                                    'on',
                                                                                ),
                                                                            },
                                                                        ]}
                                                                        selected={scheduleOnOffValue(
                                                                            currentValue[
                                                                                selectedSchedule
                                                                            ],
                                                                        )}
                                                                        onSelect={(
                                                                            val,
                                                                        ) =>
                                                                            setScheduleOnOffValue(
                                                                                selectedSchedule,
                                                                                Boolean(
                                                                                    val,
                                                                                ),
                                                                            )
                                                                        }
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            <div className="d-flex flex-row justify-content-end align-items-center mt-2">
                                                {!valuesWaiting[
                                                    selectedParamAddr
                                                ] &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                read()
                                                            }
                                                            className="text-primary ms-1"
                                                        >
                                                            <i className="fas fa-upload fa-lg" />
                                                        </span>
                                                    )}
                                                {(valuesWaiting[
                                                    selectedParamAddr
                                                ] ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]) && (
                                                    <div
                                                        className="spinner-border spinner-border-sm me-1 ms-1"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            {t('loading...')}
                                                        </span>
                                                    </div>
                                                )}
                                                {currentValue !== null &&
                                                    changed &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] &&
                                                    !selectedParam?.readOnly && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                write()
                                                            }
                                                            className="text-success ms-1"
                                                        >
                                                            <i className="fas fa-download fa-lg" />
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {['vtHolidays'].includes(
                                    selectedParam?.type,
                                ) && (
                                    <>
                                        <div className="d-flex flex-column">
                                            <div className="d-flex flex-row align-items-center">
                                                <CustomSelect
                                                    items={
                                                        !Array.isArray(
                                                            currentValue,
                                                        )
                                                            ? []
                                                            : currentValue.map(
                                                                  (
                                                                      val,
                                                                      idx,
                                                                  ) => ({
                                                                      val: idx,
                                                                      title: holidayTitle(
                                                                          val,
                                                                      ),
                                                                  }),
                                                              )
                                                    }
                                                    defaultTitle={t(
                                                        'upload_from_machine',
                                                    )}
                                                    selected={selectedSchedule}
                                                    onSelect={(s) =>
                                                        setSelectedSchedule(
                                                            Number(s),
                                                        )
                                                    }
                                                    disabled={
                                                        currentValue == null ||
                                                        valuesWriting[
                                                            selectedParamAddr
                                                        ]
                                                    }
                                                />
                                                {!valuesWaiting[
                                                    selectedParamAddr
                                                ] &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                read()
                                                            }
                                                            className="text-primary ms-1"
                                                        >
                                                            <i className="fas fa-upload fa-lg" />
                                                        </span>
                                                    )}
                                                {(valuesWaiting[
                                                    selectedParamAddr
                                                ] ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]) && (
                                                    <div
                                                        className="spinner-border spinner-border-sm me-1 ms-1"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            {t('loading...')}
                                                        </span>
                                                    </div>
                                                )}
                                                {currentValue !== null &&
                                                    changed &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] &&
                                                    !selectedParam?.readOnly && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                write()
                                                            }
                                                            className="text-success ms-1"
                                                        >
                                                            <i className="fas fa-download fa-lg" />
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                        {selectedSchedule >= 0 &&
                                            currentValue !== null && (
                                                <>
                                                    <DayPicker
                                                        key={selectedSchedule}
                                                        mode="single"
                                                        captionLayout="dropdown"
                                                        selected={
                                                            holidayDate(
                                                                currentValue[
                                                                    selectedSchedule
                                                                ],
                                                            ) ?? undefined
                                                        }
                                                        defaultMonth={
                                                            holidayDate(
                                                                currentValue[
                                                                    selectedSchedule
                                                                ],
                                                            ) ?? undefined
                                                        }
                                                        fromYear={
                                                            new Date().getFullYear() -
                                                            5
                                                        }
                                                        toYear={
                                                            new Date().getFullYear() +
                                                            5
                                                        }
                                                        onSelect={(date) => {
                                                            if (date)
                                                                holidaySetDate(
                                                                    selectedSchedule,
                                                                    date,
                                                                );
                                                        }}
                                                    />
                                                    <MDBCheckbox
                                                        type="checkbox"
                                                        id="reccurent"
                                                        checked={
                                                            (currentValue[
                                                                selectedSchedule
                                                            ] &
                                                                0x0000ffff) ==
                                                            0
                                                        }
                                                        onChange={(ev) =>
                                                            holidaySetRecurrent(
                                                                selectedSchedule,
                                                            )
                                                        }
                                                        label={t('every_year')}
                                                    />
                                                </>
                                            )}
                                    </>
                                )}
                                {['vtCoinChans'].includes(
                                    selectedParam?.type,
                                ) && (
                                    <>
                                        <div className="d-flex flex-column">
                                            {currentValue !== null && (
                                                <div className="d-flex flex-column align-items-center justify-content-center mt-2">
                                                    <div className="d-flex flex-row mb-1 justify-content-center align-items-center w-100">
                                                        <span className="d-flex flex-fill me-2 "></span>
                                                        <span
                                                            className="me-1"
                                                            style={{
                                                                width: 70,
                                                            }}
                                                        >
                                                            Pulses
                                                        </span>
                                                        <span
                                                            className="me-1"
                                                            style={{
                                                                width: 70,
                                                            }}
                                                        >
                                                            Value
                                                        </span>
                                                    </div>
                                                    {[0, 1, 2, 3, 4, 5].map(
                                                        (ch) => (
                                                            <div
                                                                key={ch}
                                                                className="d-flex flex-row mb-1 justify-content-center align-items-center w-100"
                                                            >
                                                                <span className="me-2 d-flex flex-fill">
                                                                    Ch{ch + 1}
                                                                    <span className="text-muted">
                                                                        {' '}
                                                                        (
                                                                        {((currentValue
                                                                            .vals[
                                                                            ch
                                                                        ] >>
                                                                            16) &
                                                                            0xff) *
                                                                            currentValue.scale}
                                                                        )
                                                                    </span>
                                                                </span>
                                                                <span
                                                                    className="me-1"
                                                                    style={{
                                                                        width: 70,
                                                                    }}
                                                                >
                                                                    <MDBInput
                                                                        type="number"
                                                                        name={
                                                                            'pulse' +
                                                                            ch
                                                                        }
                                                                        id={
                                                                            'pulse' +
                                                                            ch
                                                                        }
                                                                        value={
                                                                            (currentValue
                                                                                .vals[
                                                                                ch
                                                                            ] >>
                                                                                24) &
                                                                            0xff
                                                                        }
                                                                        disabled={
                                                                            valuesWriting[
                                                                                selectedParamAddr
                                                                            ]
                                                                        }
                                                                        onInput={(
                                                                            ev,
                                                                        ) => {
                                                                            const val =
                                                                                Number(
                                                                                    ev
                                                                                        .currentTarget
                                                                                        .value,
                                                                                ) &
                                                                                0xff;
                                                                            const vals =
                                                                                currentValue.vals.map(
                                                                                    (
                                                                                        el: number,
                                                                                        idx: number,
                                                                                    ) =>
                                                                                        idx !==
                                                                                        ch
                                                                                            ? el
                                                                                            : (el &
                                                                                                  0x00ffffff) +
                                                                                              (val <<
                                                                                                  24),
                                                                                );
                                                                            setCurrentValue(
                                                                                (
                                                                                    prev: number[],
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    vals,
                                                                                }),
                                                                            );
                                                                            setChanged(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    />
                                                                </span>
                                                                <span
                                                                    className="me-1"
                                                                    style={{
                                                                        width: 70,
                                                                    }}
                                                                >
                                                                    <MDBInput
                                                                        type="number"
                                                                        name={
                                                                            'val' +
                                                                            ch
                                                                        }
                                                                        id={
                                                                            'val' +
                                                                            ch
                                                                        }
                                                                        value={
                                                                            (currentValue
                                                                                .vals[
                                                                                ch
                                                                            ] >>
                                                                                16) &
                                                                            0xff
                                                                        }
                                                                        disabled={
                                                                            valuesWriting[
                                                                                selectedParamAddr
                                                                            ]
                                                                        }
                                                                        onInput={(
                                                                            ev,
                                                                        ) => {
                                                                            const val =
                                                                                Number(
                                                                                    ev
                                                                                        .currentTarget
                                                                                        .value,
                                                                                ) &
                                                                                0xff;
                                                                            const vals =
                                                                                currentValue.vals.map(
                                                                                    (
                                                                                        el: number,
                                                                                        idx: number,
                                                                                    ) =>
                                                                                        idx !==
                                                                                        ch
                                                                                            ? el
                                                                                            : (el &
                                                                                                  0xff00ffff) +
                                                                                              (val <<
                                                                                                  16),
                                                                                );
                                                                            setCurrentValue(
                                                                                (
                                                                                    prev: number[],
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    vals,
                                                                                }),
                                                                            );
                                                                            setChanged(
                                                                                true,
                                                                            );
                                                                        }}
                                                                    />
                                                                </span>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                            <div className="d-flex flex-row justify-content-end align-items-center">
                                                {!valuesWaiting[
                                                    selectedParamAddr
                                                ] &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                read()
                                                            }
                                                            className="text-primary ms-1"
                                                        >
                                                            <i className="fas fa-upload fa-lg" />
                                                        </span>
                                                    )}
                                                {(valuesWaiting[
                                                    selectedParamAddr
                                                ] ||
                                                    valuesWriting[
                                                        selectedParamAddr
                                                    ]) && (
                                                    <div
                                                        className="spinner-border spinner-border-sm me-1 ms-1"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            {t('loading...')}
                                                        </span>
                                                    </div>
                                                )}
                                                {currentValue !== null &&
                                                    changed &&
                                                    !valuesWriting[
                                                        selectedParamAddr
                                                    ] &&
                                                    !selectedParam?.readOnly && (
                                                        <span
                                                            role="button"
                                                            onClick={() =>
                                                                write()
                                                            }
                                                            className="text-success ms-1"
                                                        >
                                                            <i className="fas fa-download fa-lg" />
                                                        </span>
                                                    )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <Alert type="danger" message={error} />
                        <Alert type="success" message={writeMessage} />
                    </div>
                </>
            )}
        </CustomModal>
    );
};
